import { Suspense, useCallback, useMemo, useRef, useState } from 'react';
import { Canvas } from '@react-three/fiber';
import { Earth } from './Earth';
import { Atmosphere } from './Atmosphere';
import { Markers } from './Markers';
import { CameraRig } from './CameraRig';
import { FarSideOverlay, FarSideTracker, type FarSideNodes } from './FarSideMark';
import { HOME_VIEW } from './geo';
import { LOCATIONS } from '../data/locations';

/** The globe viewport. Stays mounted across routes; the selected city comes
 *  from the URL (passed in as a prop). No gallery UI lives inside the Canvas. */
export function GlobeCanvas({
  selectedSlug,
  onSelect,
  reducedMotion,
}: {
  selectedSlug: string | null;
  onSelect: (slug: string) => void;
  reducedMotion: boolean;
}) {
  const isCoarse = useMemo(
    () => typeof matchMedia !== 'undefined' && matchMedia('(pointer: coarse)').matches,
    [],
  );
  const selectedCity = useMemo(
    () => LOCATIONS.find((c) => c.id === selectedSlug) ?? null,
    [selectedSlug],
  );

  // Deep zoom is unlocked only once the hi-res texture is in, so the zoom limit
  // never exposes the blurry 2K preview.
  const [hiResReady, setHiResReady] = useState(false);
  const onHiRes = useCallback(() => setHiResReady(true), []);
  const farSide = useRef<FarSideNodes>({ root: null, path: null, label: null });

  return (
    <>
      <Canvas
        frameloop="demand"
        flat /* accurate satellite colors (no filmic tone-map) */
        dpr={isCoarse ? [1, 1.5] : [1, 2]}
        camera={{ position: [HOME_VIEW.x, HOME_VIEW.y, HOME_VIEW.z], fov: 39, near: 0.03, far: 20 }}
        gl={{ antialias: true, alpha: true, powerPreference: 'high-performance' }}
        shadows={false}
      >
        {/* No lights — the Earth is unlit (imagery carries its own lighting). */}
        <Suspense fallback={null}>
          <Earth onHiRes={onHiRes} />
          <Atmosphere />
          <Markers cities={LOCATIONS} selectedSlug={selectedSlug} onSelect={onSelect} />
        </Suspense>
        <CameraRig
          selectedCity={selectedCity}
          reducedMotion={reducedMotion}
          isCoarse={isCoarse}
          hiResReady={hiResReady}
        />
        <FarSideTracker nodes={farSide} selected={selectedSlug} />
      </Canvas>
      <FarSideOverlay nodes={farSide} />
    </>
  );
}
